import { type ChangeEvent, useCallback } from 'react'

import type { SageDepartment } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge } from '@/components/ui/Badge'

import styles from './Settings.module.css'
import type { SageGroup, TileDraft } from './tiles'

export interface TileHandlers {
  onRename: (code: string, name: string) => void
  onMove: (index: number, delta: -1 | 1) => void
  onToggleHidden: (code: string) => void
  onRemoveDept: (code: string, deptId: string) => void
  onRemoveTile: (code: string) => void
}

function DeptChip({
  code,
  dept,
  title,
  removable,
  onRemove,
}: {
  code: string
  dept: string
  title: string
  removable: boolean
  onRemove: (code: string, deptId: string) => void
}) {
  const handleRemove = useCallback(() => onRemove(code, dept), [onRemove, code, dept])
  return (
    <span className={styles.chip} title={title}>
      {dept}
      <button
        type="button"
        className={styles.chipButton}
        onClick={handleRemove}
        disabled={!removable}
        aria-label={`Remove ${dept} from ${code}`}
      >
        <ICONS.close size={11} />
      </button>
    </span>
  )
}

interface TileEditorProps extends TileHandlers {
  tile: TileDraft
  /** Sage sub-departments this tile picks up through a listed parent (not editable here). */
  inherited: readonly string[]
  index: number
  count: number
  departments: ReadonlyMap<string, SageDepartment>
  disabled: boolean
}

export function TileEditor({
  tile,
  inherited,
  index,
  count,
  departments,
  disabled,
  onRename,
  onMove,
  onToggleHidden,
  onRemoveDept,
  onRemoveTile,
}: TileEditorProps) {
  const handleName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onRename(tile.code, event.target.value),
    [onRename, tile.code],
  )
  const handleUp = useCallback(() => onMove(index, -1), [onMove, index])
  const handleDown = useCallback(() => onMove(index, 1), [onMove, index])
  const handleHidden = useCallback(() => onToggleHidden(tile.code), [onToggleHidden, tile.code])
  const handleRemove = useCallback(() => onRemoveTile(tile.code), [onRemoveTile, tile.code])

  return (
    <li className={`${styles.tile} ${tile.hidden ? styles.tileHidden : ''}`}>
      <div className={styles.order}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={handleUp}
          disabled={disabled || index === 0}
          aria-label={`Move ${tile.code} up`}
        >
          <ICONS.arrowUp size={13} />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={handleDown}
          disabled={disabled || index === count - 1}
          aria-label={`Move ${tile.code} down`}
        >
          <ICONS.arrowDown size={13} />
        </button>
      </div>
      <div className={styles.tileBody}>
        <div className={styles.tileHead}>
          <span className={styles.code}>{tile.code}</span>
          <input
            className={styles.nameInput}
            value={tile.name}
            onChange={handleName}
            disabled={disabled}
            maxLength={80}
            aria-label={`Name of tile ${tile.code}`}
          />
          <Badge tone="accent" small>
            Custom in FPA
          </Badge>
          {tile.hidden ? (
            <Badge tone="neutral" small>
              Hidden
            </Badge>
          ) : null}
        </div>
        <div className={styles.chips}>
          {tile.source_depts.map((dept) => (
            <DeptChip
              key={dept}
              code={tile.code}
              dept={dept}
              title={departments.get(dept)?.title ?? dept}
              removable={!disabled && tile.source_depts.length > 1}
              onRemove={onRemoveDept}
            />
          ))}
          {inherited.map((dept) => (
            <span
              key={dept}
              className={styles.chipInherited}
              title={`${departments.get(dept)?.title ?? dept}: a Sage sub-department of a department in this tile`}
            >
              <ICONS.merge size={11} />
              {dept}
            </span>
          ))}
        </div>
        {inherited.length > 0 ? (
          <div className={styles.note}>
            Plus {inherited.length} Sage sub-department{inherited.length === 1 ? '' : 's'} counted automatically.
          </div>
        ) : null}
        {tile.note ? <div className={styles.note}>{tile.note}</div> : null}
      </div>
      <div className={styles.tileActions}>
        <button type="button" className={styles.iconButton} onClick={handleHidden} disabled={disabled}>
          {tile.hidden ? <ICONS.eye size={13} /> : <ICONS.eyeOff size={13} />}
          {tile.hidden ? 'Show' : 'Hide'}
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={handleRemove}
          disabled={disabled}
          aria-label={`Remove tile ${tile.code}`}
        >
          <ICONS.trash size={13} />
          Remove tile
        </button>
      </div>
    </li>
  )
}

export function SageGroupRow({
  group,
  tiles,
  disabled,
  canEdit,
  onAdd,
  onMerge,
}: {
  group: SageGroup
  tiles: TileDraft[]
  disabled: boolean
  canEdit: boolean
  onAdd: (dept: SageDepartment) => void
  onMerge: (code: string, deptId: string) => void
}) {
  const { dept, children } = group
  const handleAdd = useCallback(() => onAdd(dept), [onAdd, dept])
  const handleMerge = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      if (event.target.value) onMerge(event.target.value, dept.id)
    },
    [onMerge, dept.id],
  )
  const inactive = dept.status !== null && dept.status.toLowerCase() !== 'active'
  return (
    <div className={styles.ungrouped}>
      <span className={styles.code}>{dept.id}</span>
      <span className={styles.deptName}>
        {dept.title}{' '}
        {group.grouping === 'sage_hierarchy' ? (
          <Badge tone="good" small>
            From Sage hierarchy · {children.length} sub-department{children.length === 1 ? '' : 's'}
          </Badge>
        ) : (
          <Badge tone="neutral" small>
            Sage department
          </Badge>
        )}{' '}
        {inactive ? (
          <Badge tone="neutral" small>
            Inactive in Sage
          </Badge>
        ) : null}
      </span>
      {canEdit ? (
        <span className={styles.groupActions}>
          <button type="button" className={styles.secondary} onClick={handleAdd} disabled={disabled}>
            <ICONS.plus size={13} />
            Make custom tile
          </button>
          <select
            className={styles.mergeSelect}
            value=""
            onChange={handleMerge}
            disabled={disabled || tiles.length === 0}
            aria-label={`Merge ${dept.id} into a tile`}
          >
            <option value="">Merge into…</option>
            {tiles.map((t) => (
              <option key={t.code} value={t.code}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
        </span>
      ) : null}
      {children.length > 0 ? (
        <div className={styles.groupChildren}>
          {children.map((child) => (
            <span key={child.id} className={styles.chipInherited} title={child.title}>
              {child.id}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
